// 21hermosa – Ingredient Analysis Engine
// Analyzes INCI ingredient lists to compute product compatibility scores automatically.
// Loaded before shopify-api.js so _nodeToProduct() can use it.

(function () {
  'use strict';

  // ─── Ingredient → Scoring Map ────────────────────────────────────────────────
  // Format: [substringToMatch (lowercase), { scoringProps }]
  // Scores accumulate additively; normalized to 0-3 at the end.
  const ING_MAP = [

    // ── FRAGRANCE DETECTION ───────────────────────────────────────────────────
    ['fragrance',   { fragFree: false }],
    ['parfum',      { fragFree: false }],
    ['flavor',      { fragFree: false }],
    ['limonene',    { fragFree: false }],
    ['linalool',    { fragFree: false }],
    ['geraniol',    { fragFree: false }],
    ['eugenol',     { fragFree: false }],
    ['citral',      { fragFree: false }],
    ['citronellol', { fragFree: false }],
    ['coumarin',    { fragFree: false }],
    ['benzyl alcohol',  { fragFree: false }],
    ['benzyl benzoate', { fragFree: false }],
    ['rosa damascena flower oil', { fragFree: false }],
    ['pelargonium graveolens', { fragFree: false }],
    ['citrus aurantium bergamia', { fragFree: false }],
    ['melaleuca alternifolia leaf oil', { fragFree: false }], // pure essential oil (not extract)

    // ── PREGNANCY RISK ────────────────────────────────────────────────────────
    ['retinol',            { pregRisk: true }],
    ['retinal',            { pregRisk: true }],  // retinaldehyde
    ['retinoic acid',      { pregRisk: true }],
    ['tretinoin',          { pregRisk: true }],
    ['salicylic acid',     { pregRisk: true }],
    ['hydroquinone',       { pregRisk: true }],
    ['phenoxyethanol',     { pregRisk: 'caution' }],  // only note, not hard block
    // Chemical UV filters (pregnancy caution)
    ['diethylamino hydroxybenzoyl hexyl benzoate', { pregRisk: true }],
    ['ethylhexyl triazone',          { pregRisk: true }],
    ['diethylhexyl butamido triazone', { pregRisk: true }],
    ['methylene bis-benzotriazolyl', { pregRisk: true }],
    ['bis-ethylhexyloxyphenol methoxyphenyl triazine', { pregRisk: true }],
    ['homosalate',           { pregRisk: true }],
    ['ethylhexyl salicylate',{ pregRisk: true }],
    ['butyloctyl salicylate',{ pregRisk: true }],
    ['butyl methoxydibenzoylmethane', { pregRisk: true }],
    ['benzophenone',         { pregRisk: true }],
    ['oxybenzone',           { pregRisk: true }],
    ['drometrizole trisiloxane', { pregRisk: true }],
    ['terephthalylidene dicamphor sulfonic acid', { pregRisk: true }],
    ['polysilicone-15',      { pregRisk: true }],
    ['ethylhexyl methoxycrylene', { pregRisk: true }],

    // ── HYALURONIC ACID FAMILY (hydration → dry skin) ─────────────────────────
    ['sodium hyaluronate crosspolymer',    { dry: 2, dryness: 2 }],
    ['sodium acetylated hyaluronate',      { dry: 2, dryness: 2 }],
    ['hydroxypropyltrimonium hyaluronate', { dry: 2, dryness: 2 }],
    ['hydrolyzed sodium hyaluronate',      { dry: 2, dryness: 2 }],
    ['hydrolyzed hyaluronic acid',         { dry: 2, dryness: 2 }],
    ['potassium hyaluronate',              { dry: 2, dryness: 2 }],
    ['sodium hyaluronate',                 { dry: 4, dryness: 4 }],
    ['hyaluronic acid',                    { dry: 3, dryness: 3 }],

    // ── HUMECTANTS ────────────────────────────────────────────────────────────
    ['panthenol',       { dry: 2, dryness: 2, sensitive: 2, redness: 1 }],
    ['beta-glucan',     { dry: 2, dryness: 2, sensitive: 3 }],
    ['ectoin',          { dry: 2, dryness: 2, sensitive: 3 }],
    ['sodium dna',      { dry: 1, thirties: 3, forties: 3 }], // PDRN
    ['sodium pca',      { dry: 2, dryness: 2 }],
    ['trehalose',       { dry: 1, dryness: 1 }],
    ['betaine',         { dry: 1, sensitive: 1 }],
    ['glycerin',        { dry: 2, dryness: 1 }],
    ['glycerine',       { dry: 2, dryness: 1 }],
    ['sorbitol',        { dry: 1, dryness: 1 }],
    ['xylitol',         { dry: 1 }],
    ['saccharide isomerate', { dry: 2, dryness: 2 }],
    ['glyceryl glucoside',   { dry: 2, dryness: 1 }],

    // ── BARRIER / LIPIDS ──────────────────────────────────────────────────────
    ['ceramide np',     { dry: 3, sensitive: 2 }],
    ['ceramide ap',     { dry: 3, sensitive: 2 }],
    ['ceramide ns',     { dry: 3, sensitive: 2 }],
    ['ceramide as',     { dry: 3, sensitive: 2 }],
    ['ceramide eg',     { dry: 2, sensitive: 2 }],
    ['ceramide',        { dry: 2, sensitive: 1 }],
    ['phytosphingosine',{ dry: 2, sensitive: 2 }],
    ['cholesterol',     { dry: 2, sensitive: 1 }],
    ['sphingolipid',    { dry: 2, sensitive: 1 }],
    ['squalane',        { dry: 2, sensitive: 1, acne: 1 }],
    ['butyrospermum parkii', { dry: 3, dryness: 3 }], // shea butter
    ['hydrogenated lecithin',{ dry: 1, sensitive: 1 }],

    // ── SNAIL MUCIN ───────────────────────────────────────────────────────────
    ['snail secretion filtrate', { dry: 2, dryness: 2, thirties: 2, forties: 2 }],

    // ── CENTELLA ASIATICA / CICA (soothing — MAJOR) ───────────────────────────
    ['centella asiatica', { sensitive: 4, redness: 4 }],
    ['madecassoside',     { sensitive: 3, redness: 3 }],
    ['asiaticoside',      { sensitive: 3, redness: 3 }],
    ['madecassic acid',   { sensitive: 2, redness: 2 }],
    ['asiatic acid',      { sensitive: 2, redness: 2 }],

    // ── HEARTLEAF / HOUTTUYNIA (oily + acne) ─────────────────────────────────
    ['houttuynia cordata', { oily: 3, acne: 3, sensitive: 2 }],

    // ── SOOTHING / ANTI-INFLAMMATORY ─────────────────────────────────────────
    ['allantoin',         { sensitive: 2, redness: 2, acne: 1 }],
    ['bisabolol',         { sensitive: 2, redness: 2 }],
    ['dipotassium glycyrrhizate', { sensitive: 2, redness: 2 }],
    ['glycyrrhiza',       { sensitive: 2, redness: 2 }],   // licorice
    ['chamomilla recutita', { sensitive: 2, redness: 2 }],
    ['portulaca oleracea',  { sensitive: 2, acne: 1 }],
    ['artemisia',           { sensitive: 2, redness: 1 }],
    ['aloe barbadensis',    { sensitive: 2, redness: 1 }],
    ['hamamelis virginiana',{ sensitive: 1, acne: 1 }],    // witch hazel

    // ── NIACINAMIDE (multi-functional) ───────────────────────────────────────
    ['niacinamide', { dullness: 3, acne: 2, oily: 1 }],

    // ── BRIGHTENING ──────────────────────────────────────────────────────────
    ['tranexamic acid',    { dullness: 4 }],
    ['alpha-arbutin',      { dullness: 3 }],
    ['arbutin',            { dullness: 2 }],
    ['glutathione',        { dullness: 3 }],
    ['3-o-ethyl ascorbic acid', { dullness: 3 }],
    ['ascorbyl glucoside', { dullness: 2 }],
    ['ascorbyl palmitate', { dullness: 2 }],
    ['sodium ascorbyl phosphate', { dullness: 2 }],
    ['ascorbic acid',      { dullness: 3 }],
    ['ferulic acid',       { dullness: 2 }],
    ['kojic acid',         { dullness: 3 }],
    ['cyanocobalamin',     { dullness: 1 }],  // vitamin B12

    // ── ACNE / OILY CONTROL ───────────────────────────────────────────────────
    ['salicylic acid',    { acne: 4, oily: 3 }],
    ['betaine salicylate',{ acne: 3, oily: 2 }],
    ['melaleuca alternifolia', { acne: 3, oily: 2 }],  // tea tree extract
    ['gluconolactone',    { acne: 2, oily: 1, dullness: 1 }],
    ['lactic acid',       { acne: 1, dullness: 2, oily: 1 }],
    ['zinc pca',          { acne: 2, oily: 3 }],
    ['zinc',              { acne: 1, oily: 1 }],
    ['sulfur',            { acne: 3, oily: 2 }],
    ['kaolin',            { oily: 2 }],
    ['bentonite',         { oily: 2 }],
    ['charcoal',          { oily: 2, acne: 1 }],

    // ── ANTI-AGING: PEPTIDES ──────────────────────────────────────────────────
    ['acetyl hexapeptide',    { thirties: 3, forties: 3 }],
    ['palmitoyl tripeptide',  { thirties: 3, forties: 3 }],
    ['palmitoyl pentapeptide',{ thirties: 3, forties: 3 }],
    ['palmitoyl tetrapeptide',{ thirties: 3, forties: 3 }],
    ['copper tripeptide',     { thirties: 3, forties: 3 }],
    ['sh-polypeptide',        { thirties: 2, forties: 3 }],
    ['oligopeptide-68',       { thirties: 2, forties: 3, dullness: 1 }],
    ['oligopeptide',          { thirties: 2, forties: 2 }],
    ['nonapeptide',           { thirties: 2, forties: 2 }],
    ['hexapeptide',           { thirties: 2, forties: 2 }],
    ['dipeptide diaminobutyroyl', { thirties: 2, forties: 3 }],
    ['nicotinamide adenine dinucleotide', { thirties: 3, forties: 3 }], // NAD+
    ['nicotinamide mononucleotide',       { thirties: 3, forties: 3 }],
    ['tranexamoyl dipeptide', { thirties: 2, forties: 2 }],

    // ── ANTI-AGING: RETINOIDS ─────────────────────────────────────────────────
    ['retinol', { thirties: 3, forties: 3, dullness: 2 }],
    ['retinal', { thirties: 3, forties: 3, dullness: 2 }],
    ['bakuchiol', { thirties: 2, forties: 3, dullness: 1, sensitive: 1 }], // retinol alternative

    // ── ANTI-AGING: OTHER ─────────────────────────────────────────────────────
    ['adenosine',           { thirties: 2, forties: 2, dullness: 1 }],
    ['panax ginseng',       { thirties: 2, forties: 3, dullness: 2, dry: 1 }],
    ['hydrolyzed collagen', { thirties: 1, forties: 2, dry: 1, dryness: 1 }],
    ['collagen extract',    { thirties: 1, forties: 2 }],
    ['soluble collagen',    { thirties: 1, forties: 2 }],
    ['elastin',             { thirties: 2, forties: 3 }],
    ['polylactic acid',     { thirties: 2, forties: 3 }],

    // ── FERMENTS / PROBIOTICS ─────────────────────────────────────────────────
    ['galactomyces ferment filtrate', { dullness: 2, oily: 1 }],
    ['lactobacillus',       { sensitive: 1, redness: 1 }],
    ['bifida ferment',      { sensitive: 2, dry: 1 }],
    ['saccharomyces ferment', { thirties: 1, forties: 1 }],

    // ── RICE / ORYZA SATIVA ───────────────────────────────────────────────────
    ['oryza sativa',    { dullness: 2, dry: 1 }],
    ['rice bran',       { dullness: 1, dry: 1 }],
    ['harina de arroz', { dullness: 2, dry: 1 }],   // Spanish common name
    ['쌀가루',           { dullness: 2, dry: 1 }],   // Korean
    ['쌀 추출물',         { dullness: 2, dry: 1 }],  // Korean
    ['쌀전분',           { dullness: 1, dry: 1 }],   // Korean

    // ── GREEN TEA / CAMELLIA SINENSIS ─────────────────────────────────────────
    ['camellia sinensis', { oily: 2, acne: 1, dullness: 1 }],
    ['녹차',              { oily: 2, acne: 1, dullness: 1 }], // Korean

    // ── WHITE TRUFFLE ─────────────────────────────────────────────────────────
    ['tuber melanosporum', { thirties: 2, forties: 2, dullness: 1 }],
    ['tuber magnatum',     { thirties: 2, forties: 2, dullness: 1 }],
    ['truffle',            { thirties: 1, forties: 1, dullness: 1 }],
    ['trufa',              { thirties: 1, forties: 1, dullness: 1 }], // Spanish
    ['트러플',              { thirties: 1, forties: 1, dullness: 1 }], // Korean

    // ── MUGWORT — specific Korean variety ─────────────────────────────────────
    ['artemisia princeps', { sensitive: 3, redness: 2 }],
    ['쑥',                 { sensitive: 2, redness: 1 }], // Korean common name

    // ── MUNG BEAN (녹두) ──────────────────────────────────────────────────────
    ['phaseolus aureus', { oily: 2, acne: 1 }],
    ['vigna radiata',    { oily: 2, acne: 1 }],
    ['녹두',              { oily: 2, acne: 1, dullness: 1 }], // Korean

    // ── TURMERIC ──────────────────────────────────────────────────────────────
    ['curcuma longa',   { dullness: 2, redness: 1 }],
    ['curcumin',        { dullness: 2, redness: 1 }],
    ['강황',             { dullness: 2, redness: 1 }], // Korean

    // ── ALOE VERA (common name alias) ─────────────────────────────────────────
    ['aloe vera',        { sensitive: 2, redness: 1, dry: 1 }],

    // ── POLYGLUTAMIC ACID ─────────────────────────────────────────────────────
    ['polyglutamic acid', { dry: 3, dryness: 3, sensitive: 1 }],

    // ── LICORICE ROOT (common alias for glycyrrhiza) ──────────────────────────
    ['licorice root',    { sensitive: 2, redness: 2, dullness: 1 }],
    ['감초',              { sensitive: 2, redness: 1, dullness: 1 }], // Korean
  ];

  // ─── Main Analysis Function ──────────────────────────────────────────────────
  function analyzeIngredients(iciString) {
    if (!iciString || typeof iciString !== 'string' || iciString.trim().length < 30) {
      return null;
    }
    const s = iciString.toLowerCase();

    const raw = {
      dry: 0, oily: 0, sensitive: 0,
      dryness: 0, acne: 0, redness: 0, dullness: 0,
      thirties: 0, forties: 0,
      fragFree: true, pregSafe: true, hasRetinoid: false
    };

    // 기능성 레티노이드 감지 (레티놀/레티날/트레티노인) — AM 안전장치용.
    // 태그가 am/pm 잘못 붙어도 이 성분이 있으면 아침 루틴에서 코드로 제외한다.
    if (/\bretinol\b/.test(s) || /\bretinal\b/.test(s) || /retinoic acid/.test(s) || /tretinoin/.test(s)) {
      raw.hasRetinoid = true;
    }

    for (const [pattern, props] of ING_MAP) {
      if (!s.includes(pattern)) continue;
      if (props.fragFree  === false)  raw.fragFree = false;
      if (props.pregRisk  === true)   raw.pregSafe = false;
      if (props.dry)      raw.dry      += props.dry;
      if (props.oily)     raw.oily     += props.oily;
      if (props.sensitive)raw.sensitive+= props.sensitive;
      if (props.dryness)  raw.dryness  += props.dryness;
      if (props.acne)     raw.acne     += props.acne;
      if (props.redness)  raw.redness  += props.redness;
      if (props.dullness) raw.dullness += props.dullness;
      if (props.thirties) raw.thirties += props.thirties;
      if (props.forties)  raw.forties  += props.forties;
    }

    // Normalize raw → 0-3 scale (divide by divisor, cap at 3, round)
    const n = (v, d = 4) => Math.min(3, Math.round(v / d));

    // Combination skin: product benefits BOTH dry and oily areas
    const comboScore = (raw.dry >= 2 && raw.oily >= 1) || (raw.oily >= 2 && raw.dry >= 1)
      ? Math.min(3, Math.round((raw.dry + raw.oily) / 5))
      : 0;

    return {
      skinTypeScore: {
        dry:         n(raw.dry),
        oily:        n(raw.oily),
        combination: comboScore,
        sensitive:   n(raw.sensitive)
      },
      concernScore: {
        dryness:  n(raw.dryness),
        acne:     n(raw.acne),
        redness:  n(raw.redness),
        dullness: n(raw.dullness),
        poros:    n(raw.acne + raw.oily, 6),          // BHA + 피지 조절 성분 → 모공 케어
        firmeza:  n(raw.thirties + raw.forties, 8),   // 항노화 성분 → 탄력/주름
        ojeras:   0                                    // 성분만으로 판별 불가, 태그/제목 기반
      },
      ageScore: {
        teen:     1,
        twenties: 2,
        thirties: Math.max(1, n(raw.thirties)),
        forties:  Math.max(1, n(raw.forties))
      },
      pregnancySafe: raw.pregSafe,
      fragranceFree: raw.fragFree,
      hasRetinoid: raw.hasRetinoid
    };
  }

  // ─── Ingredient List Extractor from HTML ────────────────────────────────────
  // Priority 1: full INCI list after "Ingredientes:" (most accurate for scoring)
  // Priority 2: highlighted "Key / Main ingredients" section in any language
  function extractFromHtml(bodyHtml) {
    if (!bodyHtml) return '';
    const text = bodyHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

    // 1. Standard INCI full list — "Ingredientes: Aqua, Glycerin, ..."
    const m1 = text.match(/Ingredientes?\s*:?\s*([A-Z][A-Za-z0-9,\/\-\(\)\[\]\.\%\+\s]{40,})/);
    if (m1) return m1[1].replace(/\s+/g, ' ').trim();

    // 2. Highlighted ingredient section — multilingual, case-insensitive
    //    Headers: "Ingredientes principales/clave/activos/star/destacados",
    //             "Key/Star/Active ingredients", "주요 성분", "핵심 성분", "주성분", "메인 성분"
    const m2 = text.match(
      /(?:ingredientes?\s*(?:principales?|clave|activos?|destacados?|star)|key\s*ingredients?|star\s*ingredients?|active\s*ingredients?|주요?\s*성분|핵심\s*성분|주성분|메인\s*성분)\s*[:\-]?\s*([\s\S]{10,400}?)(?:\.|$|(?=\n\n))/i
    );
    if (m2) return m2[1].replace(/\s+/g, ' ').trim();

    return '';
  }

  // ─── Key Ingredient Display Names + Benefits ─────────────────────────────────
  // Maps INCI substrings → { es, en, ko } names + benefit descriptions per language
  const KEY_DISPLAY_NAMES = [
    ['snail secretion filtrate', {
      es: 'Mucina de Caracol', en: 'Snail Mucin', ko: '달팽이점액여과물',
      benefit: { es: 'Regeneración y reparación cutánea profunda', en: 'Deep skin regeneration & repair', ko: '피부 재생 & 집중 수복' }
    }],
    ['sodium hyaluronate', {
      es: 'Ácido Hialurónico', en: 'Hyaluronic Acid', ko: '히알루론산',
      benefit: { es: 'Hidratación profunda multicapa', en: 'Deep multi-layer hydration', ko: '다층 깊은 수분 공급' }
    }],
    ['hyaluronic acid', {
      es: 'Ácido Hialurónico', en: 'Hyaluronic Acid', ko: '히알루론산',
      benefit: { es: 'Hidratación profunda multicapa', en: 'Deep multi-layer hydration', ko: '다층 깊은 수분 공급' }
    }],
    ['niacinamide', {
      es: 'Niacinamida', en: 'Niacinamide', ko: '나이아신아마이드',
      benefit: { es: 'Afina poros y unifica el tono de piel', en: 'Minimizes pores & evens skin tone', ko: '모공 축소 & 피부 톤 균일화' }
    }],
    ['madecassoside', {
      es: 'Madecassoside', en: 'Madecassoside', ko: '마데카소사이드',
      benefit: { es: 'Cicatriza y calma la irritación activa', en: 'Heals & calms active irritation', ko: '피부 진정 & 상처 회복' }
    }],
    ['asiaticoside', {
      es: 'Asiaticoside', en: 'Asiaticoside', ko: '아시아티코사이드',
      benefit: { es: 'Refuerza la barrera cutánea', en: 'Strengthens the skin barrier', ko: '피부 장벽 강화' }
    }],
    ['centella asiatica', {
      es: 'Centella Asiática', en: 'Centella Asiatica', ko: '병풀 추출물',
      benefit: { es: 'Calma e hidrata pieles reactivas y sensibles', en: 'Soothes & hydrates reactive/sensitive skin', ko: '민감성 피부 진정 & 보습' }
    }],
    ['houttuynia cordata', {
      es: 'Heartleaf (어성초)', en: 'Heartleaf Extract', ko: '어성초 추출물',
      benefit: { es: 'Control activo de acné y exceso de sebo', en: 'Active acne & sebum control', ko: '여드름 & 피지 집중 케어' }
    }],
    ['propolis', {
      es: 'Propóleo', en: 'Propolis', ko: '프로폴리스',
      benefit: { es: 'Antibacteriano + nutrición y brillo intenso', en: 'Antibacterial + deep nourishment & glow', ko: '항균 + 집중 영양 & 광채' }
    }],
    ['panthenol', {
      es: 'Pantenol (Pro-Vit. B5)', en: 'Panthenol (Pro-Vit. B5)', ko: '판테놀(프로비타민B5)',
      benefit: { es: 'Hidrata y regenera la piel dañada', en: 'Hydrates & repairs damaged skin', ko: '손상 피부 보습 & 재생' }
    }],
    ['allantoin', {
      es: 'Alantoína', en: 'Allantoin', ko: '알란토인',
      benefit: { es: 'Suaviza y calma la piel irritada', en: 'Softens & soothes irritated skin', ko: '피부 진정 & 부드러운 피부 결' }
    }],
    ['tranexamic acid', {
      es: 'Ácido Tranexámico', en: 'Tranexamic Acid', ko: '트라넥삼산',
      benefit: { es: 'Aclara manchas y unifica el tono', en: 'Fades dark spots & evens tone', ko: '잡티 개선 & 피부 톤 균일화' }
    }],
    ['arbutin', {
      es: 'Arbutina', en: 'Arbutin', ko: '알부틴',
      benefit: { es: 'Ilumina y aclara las manchas oscuras', en: 'Brightens & fades dark spots', ko: '미백 & 잡티 개선' }
    }],
    ['ascorbic acid', {
      es: 'Vitamina C (Ascórbico)', en: 'Vitamin C (Ascorbic)', ko: '비타민C(아스코르빅)',
      benefit: { es: 'Luminosidad + potente antioxidante', en: 'Radiance + powerful antioxidant', ko: '광채 + 강력 항산화' }
    }],
    ['retinol', {
      es: 'Retinol', en: 'Retinol', ko: '레티놀',
      benefit: { es: 'Reduce arrugas y renueva las células cutáneas', en: 'Reduces wrinkles & renews skin cells', ko: '주름 개선 + 세포 재생' }
    }],
    ['betaine salicylate', {
      es: 'Betaínato de Salicilo (BHA)', en: 'Betaine Salicylate (BHA)', ko: '베타인살리실레이트(BHA)',
      benefit: { es: 'Exfoliante suave que libera los poros', en: 'Gentle exfoliant that unclogs pores', ko: '순한 각질 제거 & 모공 클렌징' }
    }],
    ['salicylic acid', {
      es: 'Ácido Salicílico', en: 'Salicylic Acid', ko: '살리실산',
      benefit: { es: 'Elimina el acné y desatasca poros en profundidad', en: 'Fights acne & deep-clears pores', ko: '여드름 완화 & 모공 딥 클렌징' }
    }],
    ['glycolic acid', {
      es: 'Ácido Glicólico', en: 'Glycolic Acid', ko: '글라이콜릭애씨드',
      benefit: { es: 'Exfolia, ilumina y renueva la piel', en: 'Exfoliates, brightens & renews skin', ko: '각질 제거 & 피부 재생 + 미백' }
    }],
    ['adenosine', {
      es: 'Adenosina', en: 'Adenosine', ko: '아데노신',
      benefit: { es: 'Antiarrugas: mejora firmeza y elasticidad', en: 'Anti-wrinkle: boosts firmness & elasticity', ko: '주름 개선 & 탄력 강화' }
    }],
    ['ceramide', {
      es: 'Ceramida', en: 'Ceramide', ko: '세라마이드',
      benefit: { es: 'Restaura y refuerza la barrera cutánea', en: 'Restores & strengthens the skin barrier', ko: '피부 장벽 강화 & 복구' }
    }],
    ['peptide', {
      es: 'Péptidos', en: 'Peptides', ko: '펩타이드',
      benefit: { es: 'Estimula el colágeno y reafirma la piel', en: 'Stimulates collagen & firms the skin', ko: '콜라겐 촉진 & 피부 탄력 강화' }
    }],
    ['palmitoyl', {
      es: 'Péptidos Activos', en: 'Active Peptides', ko: '활성 펩타이드',
      benefit: { es: 'Péptidos de señal que reafirman la piel', en: 'Signal peptides that firm the skin', ko: '피부 탄력을 높이는 활성 펩타이드' }
    }],
    ['panax ginseng', {
      es: 'Extracto de Ginseng', en: 'Ginseng Extract', ko: '인삼 추출물',
      benefit: { es: 'Antioxidante + energiza y regenera la piel', en: 'Antioxidant + energizes & regenerates skin', ko: '항산화 + 피부 활력 & 재생' }
    }],
    ['zinc oxide', {
      es: 'Óxido de Zinc (Físico)', en: 'Zinc Oxide (Mineral)', ko: '징크옥사이드(무기자차)',
      benefit: { es: 'Filtro solar mineral suave, ideal pieles sensibles', en: 'Gentle mineral UV filter, sensitive-skin safe', ko: '무기자차: 민감 피부도 OK' }
    }],
    ['beta-glucan', {
      es: 'Beta-Glucano', en: 'Beta-Glucan', ko: '베타글루칸',
      benefit: { es: 'Hidratación intensa y efecto calmante duradero', en: 'Intense hydration & lasting soothing effect', ko: '강력 보습 & 피부 진정' }
    }],
    ['bifida ferment', {
      es: 'Fermentos de Bifida', en: 'Bifida Ferment Lysate', ko: '비피다발효여과물',
      benefit: { es: 'Refuerza la microbiota de la piel', en: 'Strengthens the skin microbiome', ko: '피부 마이크로바이옴 강화' }
    }],
    ['galactomyces', {
      es: 'Galactomyces Ferment', en: 'Galactomyces Ferment', ko: '갈락토마이세스 발효여과물',
      benefit: { es: 'Ilumina la piel y minimiza los poros', en: 'Brightens skin & minimizes pores', ko: '피부 광채 & 모공 축소' }
    }],
    ['saccharomyces', {
      es: 'Fermentos de Levadura', en: 'Saccharomyces Ferment', ko: '사카로마이세스 발효여과물',
      benefit: { es: 'Fermentos que nutren y revitalizan la piel', en: 'Ferments that nourish & revitalize skin', ko: '영양 공급 & 피부 활력 발효 성분' }
    }],
    ['caffeine', {
      es: 'Cafeína', en: 'Caffeine', ko: '카페인',
      benefit: { es: 'Activa la circulación y reduce las ojeras', en: 'Boosts circulation & reduces dark circles', ko: '혈액 순환 촉진 & 다크서클 완화' }
    }],
    ['melaleuca alternifolia', {
      es: 'Árbol de Té (extracto)', en: 'Tea Tree Extract', ko: '티트리 추출물',
      benefit: { es: 'Antibacteriano que reduce granos y rojeces', en: 'Antibacterial that reduces blemishes & redness', ko: '항균 & 여드름·홍조 케어' }
    }],
    ['tea tree', {
      es: 'Árbol de Té', en: 'Tea Tree', ko: '티트리',
      benefit: { es: 'Purifica y controla el exceso de sebo', en: 'Purifies & controls excess sebum', ko: '피부 정화 & 피지 조절' }
    }],
    ['rice', {
      es: 'Extracto de Arroz', en: 'Rice Extract', ko: '쌀 추출물',
      benefit: { es: 'Ilumina y aporta nutrición suave', en: 'Brightens & gently nourishes skin', ko: '쌀 성분: 미백 & 영양 공급' }
    }],
    ['oryza sativa', {
      es: 'Harina/Almidón de Arroz', en: 'Rice Flour / Starch', ko: '쌀가루/쌀전분',
      benefit: { es: 'Exfolia suavemente y aporta luminosidad natural', en: 'Gently exfoliates & boosts natural radiance', ko: '순한 각질 제거 & 자연스러운 광채' }
    }],
    ['harina de arroz', {
      es: 'Harina de Arroz', en: 'Rice Flour', ko: '쌀가루',
      benefit: { es: 'Exfolia suavemente y aporta luminosidad natural', en: 'Gently exfoliates & boosts natural radiance', ko: '순한 각질 제거 & 자연스러운 광채' }
    }],
    ['쌀가루', {
      es: 'Harina de Arroz', en: 'Rice Flour', ko: '쌀가루',
      benefit: { es: 'Exfolia suavemente y aporta luminosidad natural', en: 'Gently exfoliates & boosts natural radiance', ko: '순한 각질 제거 & 자연스러운 광채' }
    }],
    ['truffle', {
      es: 'Extracto de Trufa Blanca', en: 'White Truffle Extract', ko: '화이트 트러플 추출물',
      benefit: { es: 'Antioxidante premium que ilumina y regenera', en: 'Premium antioxidant that brightens & regenerates', ko: '프리미엄 항산화 & 피부 재생·광채' }
    }],
    ['trufa', {
      es: 'Extracto de Trufa', en: 'Truffle Extract', ko: '트러플 추출물',
      benefit: { es: 'Antioxidante premium que ilumina y regenera', en: 'Premium antioxidant that brightens & regenerates', ko: '프리미엄 항산화 & 피부 재생·광채' }
    }],
    ['트러플', {
      es: 'Trufa Blanca', en: 'White Truffle', ko: '화이트 트러플',
      benefit: { es: 'Antioxidante premium que ilumina y regenera', en: 'Premium antioxidant that brightens & regenerates', ko: '프리미엄 항산화 & 피부 재생·광채' }
    }],
    ['camellia sinensis', {
      es: 'Té Verde', en: 'Green Tea Extract', ko: '녹차 추출물',
      benefit: { es: 'Antioxidante rico en catequinas que controla el sebo', en: 'Catechin-rich antioxidant that controls sebum', ko: '카테킨 항산화 & 피지 조절' }
    }],
    ['녹차', {
      es: 'Té Verde', en: 'Green Tea', ko: '녹차',
      benefit: { es: 'Antioxidante rico en catequinas que controla el sebo', en: 'Catechin-rich antioxidant that controls sebum', ko: '카테킨 항산화 & 피지 조절' }
    }],
    ['artemisia princeps', {
      es: 'Artemisia (Mugwort)', en: 'Mugwort Extract', ko: '쑥 추출물',
      benefit: { es: 'Calma la piel sensible y reduce el enrojecimiento', en: 'Calms sensitive skin & reduces redness', ko: '민감 피부 진정 & 홍조 케어' }
    }],
    ['쑥', {
      es: 'Artemisia (Mugwort)', en: 'Mugwort', ko: '쑥',
      benefit: { es: 'Calma la piel sensible y reduce el enrojecimiento', en: 'Calms sensitive skin & reduces redness', ko: '민감 피부 진정 & 홍조 케어' }
    }],
    ['aloe vera', {
      es: 'Aloe Vera', en: 'Aloe Vera', ko: '알로에 베라',
      benefit: { es: 'Calma, hidrata y refresca la piel irritada', en: 'Soothes, hydrates & cools irritated skin', ko: '진정·보습·쿨링 효과' }
    }],
    ['녹두', {
      es: 'Judía Mungo (Mung Bean)', en: 'Mung Bean', ko: '녹두',
      benefit: { es: 'Exfolia y controla el exceso de sebo naturalmente', en: 'Naturally exfoliates & controls excess sebum', ko: '천연 각질 제거 & 피지 조절' }
    }],
    ['curcuma', {
      es: 'Cúrcuma', en: 'Turmeric', ko: '강황',
      benefit: { es: 'Antiinflamatorio que ilumina y uniformiza el tono', en: 'Anti-inflammatory that brightens & evens tone', ko: '항염 & 미백 효과' }
    }],
    ['polyglutamic acid', {
      es: 'Ácido Poliglutámico', en: 'Polyglutamic Acid', ko: '폴리글루타민산',
      benefit: { es: 'Retiene 4x más agua que el ácido hialurónico', en: 'Holds 4x more water than hyaluronic acid', ko: '히알루론산의 4배 수분 보유력' }
    }],
    ['glycyrrhiza', {
      es: 'Raíz de Regaliz', en: 'Licorice Root', ko: '감초 추출물',
      benefit: { es: 'Ilumina, calma y reduce la hiperpigmentación', en: 'Brightens, soothes & reduces hyperpigmentation', ko: '미백 & 진정 & 색소 침착 완화' }
    }],
    ['squalane', {
      es: 'Escualano', en: 'Squalane', ko: '스쿠알란',
      benefit: { es: 'Aceite ligero que hidrata sin poros obstruidos', en: 'Lightweight oil that hydrates without clogging', ko: '가벼운 보습 오일, 모공 무부담' }
    }],
    ['bakuchiol', {
      es: 'Bakuchiol (Retinol Natural)', en: 'Bakuchiol (Natural Retinol)', ko: '바쿠치올(천연 레티놀)',
      benefit: { es: 'Alternativa natural al retinol, apta para embarazadas', en: 'Natural retinol alternative, pregnancy-safe', ko: '임산부도 OK인 천연 레티놀 대체 성분' }
    }],

    // ── SPANISH/KOREAN COMMON NAMES (from product descriptions) ──────────────
    // ⚠️ 스페인어/한국어 설명에서 hero 성분이 잡히도록 별칭 추가 (영어 INCI만으론 놓침)
    ['ceramida', {
      es: 'Ceramida', en: 'Ceramide', ko: '세라마이드',
      benefit: { es: 'Restaura y refuerza la barrera cutánea', en: 'Restores & strengthens the skin barrier', ko: '피부 장벽 강화 & 복구' }
    }],
    ['세라마이드', {
      es: 'Ceramida', en: 'Ceramide', ko: '세라마이드',
      benefit: { es: 'Restaura y refuerza la barrera cutánea', en: 'Restores & strengthens the skin barrier', ko: '피부 장벽 강화 & 복구' }
    }],
    ['pantenol', {
      es: 'Pantenol (Pro-Vit. B5)', en: 'Panthenol (Pro-Vit. B5)', ko: '판테놀(프로비타민B5)',
      benefit: { es: 'Hidrata y regenera la piel dañada', en: 'Hydrates & repairs damaged skin', ko: '손상 피부 보습 & 재생' }
    }],
    ['판테놀', {
      es: 'Pantenol (Pro-Vit. B5)', en: 'Panthenol (Pro-Vit. B5)', ko: '판테놀(프로비타민B5)',
      benefit: { es: 'Hidrata y regenera la piel dañada', en: 'Hydrates & repairs damaged skin', ko: '손상 피부 보습 & 재생' }
    }],
    ['té verde', {
      es: 'Té Verde', en: 'Green Tea', ko: '녹차',
      benefit: { es: 'Antioxidante rico en catequinas, controla el sebo', en: 'Catechin-rich antioxidant, controls sebum', ko: '카테킨 항산화 & 피지 조절' }
    }],
    ['polvo de arroz', {
      es: 'Polvo de Arroz', en: 'Rice Powder', ko: '쌀가루',
      benefit: { es: 'Exfolia suavemente y aporta luminosidad natural', en: 'Gently exfoliates & adds natural radiance', ko: '순한 각질 제거 & 자연 광채' }
    }],
    ['caolín', {
      es: 'Caolín (Arcilla)', en: 'Kaolin Clay', ko: '카올린 클레이',
      benefit: { es: 'Absorbe el exceso de sebo y limpia los poros en profundidad', en: 'Absorbs excess oil & deep-cleanses pores', ko: '피지 흡수 & 모공 딥 클렌징' }
    }],
    ['kaolin', {
      es: 'Caolín (Arcilla)', en: 'Kaolin Clay', ko: '카올린 클레이',
      benefit: { es: 'Absorbe el exceso de sebo y limpia los poros en profundidad', en: 'Absorbs excess oil & deep-cleanses pores', ko: '피지 흡수 & 모공 딥 클렌징' }
    }],
    ['extracto de arroz', {
      es: 'Extracto de Arroz', en: 'Rice Extract', ko: '쌀 추출물',
      benefit: { es: 'Ilumina y aporta nutrición suave a la piel', en: 'Brightens & gently nourishes skin', ko: '미백 & 부드러운 영양 공급' }
    }],
    ['agua de arroz', {
      es: 'Agua de Arroz', en: 'Rice Water', ko: '쌀뜨물',
      benefit: { es: 'Ilumina, hidrata y suaviza la textura de la piel', en: 'Brightens, hydrates & smooths skin texture', ko: '미백 & 보습 & 피부결 개선' }
    }],
    ['centella', {
      es: 'Centella Asiática', en: 'Centella Asiatica', ko: '센텔라(병풀)',
      benefit: { es: 'Calma e hidrata pieles reactivas y sensibles', en: 'Soothes & hydrates reactive/sensitive skin', ko: '민감성 피부 진정 & 보습' }
    }],
    ['cica', {
      es: 'CICA (Centella)', en: 'CICA (Centella)', ko: 'CICA(병풀)',
      benefit: { es: 'Calma la irritación y repara la barrera cutánea', en: 'Calms irritation & repairs the skin barrier', ko: '자극 완화 & 피부 장벽 복구' }
    }],
    ['ácido hialurónico', {
      es: 'Ácido Hialurónico', en: 'Hyaluronic Acid', ko: '히알루론산',
      benefit: { es: 'Hidratación profunda multicapa', en: 'Deep multi-layer hydration', ko: '다층 깊은 수분 공급' }
    }],
    ['mucina de caracol', {
      es: 'Mucina de Caracol', en: 'Snail Mucin', ko: '달팽이점액',
      benefit: { es: 'Regeneración y reparación cutánea profunda', en: 'Deep skin regeneration & repair', ko: '피부 재생 & 집중 수복' }
    }],
    ['vitamina c', {
      es: 'Vitamina C', en: 'Vitamin C', ko: '비타민C',
      benefit: { es: 'Luminosidad intensa y antioxidante potente', en: 'Intense radiance & powerful antioxidant', ko: '집중 광채 & 강력 항산화' }
    }],
    ['비타민c', {
      es: 'Vitamina C', en: 'Vitamin C', ko: '비타민C',
      benefit: { es: 'Luminosidad intensa y antioxidante potente', en: 'Intense radiance & powerful antioxidant', ko: '집중 광채 & 강력 항산화' }
    }],
    ['propóleo', {
      es: 'Propóleo', en: 'Propolis', ko: '프로폴리스',
      benefit: { es: 'Antibacteriano + nutrición y brillo intenso', en: 'Antibacterial + deep nourishment & glow', ko: '항균 + 집중 영양 & 광채' }
    }],
    ['extracto de propóleo', {
      es: 'Propóleo', en: 'Propolis', ko: '프로폴리스',
      benefit: { es: 'Antibacteriano + nutrición y brillo intenso', en: 'Antibacterial + deep nourishment & glow', ko: '항균 + 집중 영양 & 광채' }
    }],
    ['niacinamida', {
      es: 'Niacinamida', en: 'Niacinamide', ko: '나이아신아마이드',
      benefit: { es: 'Afina poros y unifica el tono de piel', en: 'Minimizes pores & evens skin tone', ko: '모공 축소 & 피부 톤 균일화' }
    }],
    ['어성초', {
      es: 'Heartleaf (어성초)', en: 'Heartleaf Extract', ko: '어성초 추출물',
      benefit: { es: 'Control activo de acné y exceso de sebo', en: 'Active acne & sebum control', ko: '여드름 & 피지 집중 케어' }
    }],
    ['인삼', {
      es: 'Extracto de Ginseng', en: 'Ginseng Extract', ko: '인삼 추출물',
      benefit: { es: 'Antioxidante + energiza y regenera la piel', en: 'Antioxidant + energizes & regenerates skin', ko: '항산화 + 피부 활력 & 재생' }
    }],
    ['히알루론산', {
      es: 'Ácido Hialurónico', en: 'Hyaluronic Acid', ko: '히알루론산',
      benefit: { es: 'Hidratación profunda multicapa', en: 'Deep multi-layer hydration', ko: '다층 깊은 수분 공급' }
    }],
    ['달팽이', {
      es: 'Mucina de Caracol', en: 'Snail Mucin', ko: '달팽이점액',
      benefit: { es: 'Regeneración y reparación cutánea profunda', en: 'Deep skin regeneration & repair', ko: '피부 재생 & 집중 수복' }
    }],
  ];

  // ─── Extract Human-Readable Key Ingredients from INCI String ─────────────────
  // Returns up to maxCount ingredient names joined as a string (for display tags).
  // 어디에나 들어가는 "보조" 성분 — hero로 잘 안 뽑히게 후순위(제품명에 있으면 예외)
  const COMMON_SUPPORTING = new Set(['Niacinamida', 'Pantenol (Pro-Vit. B5)', 'Alantoína', 'Adenosina']);
  // 우선순위: 제품명 등장(0) < 개성 hero(1) < 보조 성분(2), 동순위는 등장 위치순
  function _heroRank(es, pos, titleLen) {
    if (pos < titleLen) return 0;
    return COMMON_SUPPORTING.has(es) ? 2 : 1;
  }
  function _collectByName(s) {
    const byName = new Map();
    for (const [pattern, names] of KEY_DISPLAY_NAMES) {
      const pos = s.indexOf(pattern);
      if (pos === -1) continue;
      const prev = byName.get(names.es);
      if (!prev || pos < prev.pos) byName.set(names.es, { pos, names });
    }
    return byName;
  }
  function _sortHero(byName, titleLen) {
    return [...byName.values()].sort((a, b) => {
      const ra = _heroRank(a.names.es, a.pos, titleLen), rb = _heroRank(b.names.es, b.pos, titleLen);
      return ra !== rb ? ra - rb : a.pos - b.pos;
    });
  }

  function getHighlightIngredients(iciString, lang, maxCount, titleLen) {
    lang = lang || 'es';
    maxCount = maxCount || 4;
    titleLen = titleLen || 0;
    if (!iciString) return '';
    const sorted = _sortHero(_collectByName(iciString.toLowerCase()), titleLen);
    return sorted.slice(0, maxCount).map(({ names }) => names[lang] || names.es).join(', ');
  }

  // ─── Extract Ingredient Highlights with Benefits ──────────────────────────────
  // Returns up to maxCount objects: { name: {es,en,ko}, benefit: {es,en,ko} }
  function getIngredientHighlights(iciString, maxCount, titleLen) {
    maxCount = maxCount || 3;
    titleLen = titleLen || 0;
    if (!iciString) return [];
    const sorted = _sortHero(_collectByName(iciString.toLowerCase()), titleLen);
    return sorted.slice(0, maxCount).map(({ names }) => ({
      name:    { es: names.es, en: names.en || names.es, ko: names.ko || names.es },
      benefit: names.benefit || { es: '', en: '', ko: '' }
    }));
  }

  // ─── Full Description Text Extractor (display-only fallback) ────────────────
  // Returns the full plain text of the product description for keyword scanning.
  // Used when no structured INCI list or "key ingredients" section is found.
  // NOTE: Only used for ingredient DISPLAY (highlights), NOT for scoring.
  function extractDisplayText(bodyHtml) {
    if (!bodyHtml) return '';
    return bodyHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  window.ingredientEngine = { analyzeIngredients, extractFromHtml, extractDisplayText, getHighlightIngredients, getIngredientHighlights };
})();
